

import { Schedule, InheritedResource, Direction } from '../types';

/**
 * Computes inherited resources for each node in the graph.
 * 
 * Direction 0 (Bottom Up): Inherits resources from all Parents (recursive).
 * Direction 1 (Top Down): Inherits resources from all Children (recursive).
 */
export const computeInheritance = (schedules: Schedule[]): Map<string, InheritedResource[]> => {
  const nodeMap = new Map(schedules.map(s => [s.scheduleId, s]));
  const result = new Map<string, InheritedResource[]>();

  schedules.forEach(node => {
    const inherited: InheritedResource[] = [];
    const visited = new Set<string>();
    visited.add(node.scheduleId);

    // Initialize queue based on direction
    // If Bottom Up (0), we start traversing up to Parents
    // If Top Down (1), we start traversing down to Children
    const queue = node.direction === Direction.TopDown 
      ? [...node.children] 
      : [...node.parents];

    while (queue.length > 0) {
      const targetId = queue.shift()!;
      if (visited.has(targetId)) continue;
      visited.add(targetId);

      const target = nodeMap.get(targetId);
      if (!target) continue;

      // Add target's resources to the inherited list
      target.resources.forEach(res => {
        inherited.push({
          ...res,
          sourceNodeId: target.scheduleId,
          sourceNodeName: target.name,
          sourceNodePriority: target.priority,
          isInherited: true
        });
      });

      // Continue traversal recursively
      if (node.direction === Direction.TopDown) {
        // If we are looking down, continue to children
        queue.push(...target.children);
      } else {
        // If we are looking up, continue to parents
        queue.push(...target.parents);
      }
    }
    
    result.set(node.scheduleId, inherited);
  });

  return result;
};